import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BULK_ASSET_JOB_CREATION_KEY } from '~/constants/bulkAssetOperations';
import { MultiActionButton } from '~/components/form/MultiActionButton';
import { Asset } from '~/routes/assets';
import { BulkInactivate } from './BulkInactivate';

type Props = {
  selected: string[];
  selectedAssets: Asset[];
  onClearSelection: () => void;
  reload: () => void;
};

/**
 * Get unique properties (deployment locations) from selected assets
 */
const getUniqueProperties = (assets: Asset[]) => {
  const propertyMap = new Map<string, { id: string; name: string }>();
  const assetsWithoutDeployment: Asset[] = [];

  assets.forEach((asset) => {
    const locationId = asset.deployment?.location?.id;
    const locationName = asset.deployment?.location?.name;

    if (locationId && locationName) {
      if (!propertyMap.has(locationId)) {
        propertyMap.set(locationId, { id: locationId, name: locationName });
      }
    } else {
      assetsWithoutDeployment.push(asset);
    }
  });

  return {
    properties: Array.from(propertyMap.values()),
    assetsWithoutDeployment,
  };
};

/**
 * Mobile-specific FAB with split button for bulk asset actions
 * Shows when assets are selected on mobile
 */
export const MobileBulkAssetFAB = ({
  selected,
  selectedAssets,
  onClearSelection,
  reload,
}: Props) => {
  const navigate = useNavigate();
  const [showInactivateConfirm, setShowInactivateConfirm] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // Get unique properties from selected assets
  const { properties } = useMemo(
    () => getUniqueProperties(selectedAssets),
    [selectedAssets]
  );

  // Assets with deployments that can be used for job creation
  const assetsWithDeployment = useMemo(
    () => selectedAssets.filter((a) => a.deployment?.location?.id),
    [selectedAssets]
  );

  // Determine if job creation is allowed:
  // - Must have exactly one property
  // - Must have at least one asset with deployment
  const canCreateJob =
    properties.length === 1 && assetsWithDeployment.length > 0;

  // Reason why job creation is disabled (only for multiple properties)
  const showMultiplePropertiesBanner =
    properties.length > 1 && !isBannerDismissed;

  // Reset banner dismissal when selection changes
  useEffect(() => {
    setIsBannerDismissed(false);
  }, [selected.length, properties.length]);

  // Handle create jobs action (primary action)
  const handleCreateJobs = () => {
    if (!canCreateJob) {
      // Show toast explaining why job creation is disabled
      if (properties.length > 1) {
        toast.warning('Assets must be from the same property to create a job', {
          autoClose: 5000,
        });
      } else if (assetsWithDeployment.length === 0) {
        toast.warning('Selected assets have no deployment location', {
          autoClose: 5000,
        });
      }
      return;
    }

    // Store selected assets in session storage for the job form to pick up
    // Include asset type and space info for creating asset tasks
    // Only include assets with deployments (filtered subset) for consistency
    const assetIdsWithDeployment = assetsWithDeployment.map((a) => a.id);
    sessionStorage.setItem(
      BULK_ASSET_JOB_CREATION_KEY,
      JSON.stringify({
        assetIds: assetIdsWithDeployment,
        assets: assetsWithDeployment.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type,
          deploymentLocationId: a.deployment?.location?.id,
          deploymentLocationName: a.deployment?.location?.name,
          deploymentSpaceName: a.deployment?.space?.name,
        })),
      })
    );

    // Navigate to bulk job creation form as outlet (keeps assets list visible)
    navigate('/assets/jobs/new', {
      state: {
        fromBulkAssets: true,
        assetIds: assetIdsWithDeployment,
      },
      replace: true, // Replace current history entry to avoid back button issues
    });

    // Don't clear selection - keep assets selected
  };

  // Handle make inactive success
  const handleInactivateSuccess = () => {
    toast.success(`${selected.length} assets made inactive`);
    setShowInactivateConfirm(false);
    reload();
    onClearSelection();
  };

  return (
    <>
      {/* Confirmation dialog for make inactive */}
      {showInactivateConfirm && (
        <BulkInactivate
          selected={selected}
          selectedAssets={selectedAssets}
          onCancel={() => setShowInactivateConfirm(false)}
          onSuccess={handleInactivateSuccess}
        />
      )}

      {/* FAB container with gradient */}
      <div className='pointer-events-none fixed bottom-0 left-0 right-0 z-30 flex h-1/4 justify-center md:h-1/5'>
        {/* Warning banner for multiple properties - positioned above FAB */}
        {showMultiplePropertiesBanner && (
          <div className='pointer-events-auto absolute bottom-44 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 transform items-start gap-3 rounded-lg border border-orange/60 bg-amber-50 p-3 pl-4 text-orange shadow-lg'>
            <FontAwesomeIcon
              icon={faExclamationTriangle as IconProp}
              className='mt-0.5 h-5 w-5 flex-shrink-0 rounded-full border-2 border-orange/30 bg-white/10 p-2'
            />
            <div className='flex-1'>
              <p className='mb-2 text-sm'>
                You can only create a job for assets from one place at a time
              </p>
              <button
                onClick={onClearSelection}
                className='text-sm font-medium underline hover:text-orange/80'
              >
                Clear selection
              </button>
            </div>
            <button
              onClick={() => setIsBannerDismissed(true)}
              className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full hover:bg-orange/20'
              aria-label='Dismiss banner'
            >
              <FontAwesomeIcon icon={faXmark} className='h-4 w-4' />
            </button>
          </div>
        )}

        {/* Split FAB button - reuses MultiActionButton with fab variant */}
        <MultiActionButton
          variant='fab'
          className='absolute -right-12 bottom-28 z-50 -translate-x-1/2 transform'
          options={[
            {
              label: 'Job',
              onClick: handleCreateJobs,
              disabled: !canCreateJob,
              icon: <FontAwesomeIcon icon={faPlus} className='h-4 w-4' />,
            },
            {
              label: 'Make Inactive',
              onClick: () => setShowInactivateConfirm(true),
            },
          ]}
        />

        {/* Background gradient */}
        <div className='absolute inset-0 bg-gradient-to-t from-white md:via-white' />
      </div>
    </>
  );
};
